import * as THREE from "three";
import type { WorldEntity } from "../../data/model";
import { buildSolCoin } from "./coin";
import { buildEthCoin } from "./ethCoin";
import { buildTruck } from "./truck";
import { buildRamp } from "./ramp";
import { buildBarrierBar, buildBarrierHigh, buildBarrierLow } from "./barriers";
import { animateGasCop } from "./cop";
import { buildKey, buildLetter, buildMysteryBox, buildPowerPickup, buildSeasonToken } from "./pickups";
import { buildBridgeDeck, buildGateWall } from "./bridge";
import { buildBush, buildDumpster, buildPillar, buildPowerBox, buildTrafficPole } from "./street";

/**
 * SOL Run props — studio-look builders for everything on the road. Every builder returns a fresh clone of a cached
 * prototype (geometry and materials shared), origin = the entity's footprint centre on the road unless noted, front
 * (the side the player meets) = −z, text reads correctly through the scene's VX mirror (it mirrors positions only).
 */
export { buildSolCoin } from "./coin";
export { buildEthCoin } from "./ethCoin";
export { buildTruck, CONTAINER_COLORS, type TruckOptions } from "./truck";
export { buildRamp } from "./ramp";
export { buildBarrierLow, buildBarrierBar, buildBarrierHigh } from "./barriers";
export { buildGasCop, animateGasCop, type GasCopParts } from "./cop";
export { buildPowerPickup, buildKey, buildMysteryBox, buildLetter, buildSeasonToken, buildBoardPickup, PICKUP_COLORS } from "./pickups";
export { buildBridgeDeck, buildGateWall } from "./bridge";
export { buildDumpster, buildPowerBox, buildTrafficPole, buildBush, buildPillar } from "./street";
export { buildHoverBoard, BOARD_COLORS } from "./boards";
export { triangles, drawCalls } from "./kit";

/**
 * One call for createEntityObject: the new model for an entity, or null for kinds this set doesn't draw (gate,
 * blocker, platform, speed pads). `variant` = the scene's truck colour variant (entity.id % 3).
 */
export function buildEntityProp(entity: Pick<WorldEntity, "kind" | "trailers" | "power" | "letter">, variant = 0): THREE.Group | null {
  switch (entity.kind) {
    case "coin": return buildSolCoin();
    case "ethCoin": return buildEthCoin();
    case "truck": return buildTruck({ trailers: entity.trailers, variant });
    case "mover": return buildTruck({ trailers: entity.trailers, variant, oncoming: true });
    case "ramp": return buildRamp();
    case "barrierLow": return buildBarrierLow();
    case "barrierBar": return buildBarrierBar();
    case "barrierHigh": return buildBarrierHigh();
    case "pickup": return buildPowerPickup(entity.power ?? "magnet");
    case "key": return buildKey();
    case "mysteryBox": return buildMysteryBox();
    case "letter": return buildLetter(entity.letter ?? "A");
    case "token": return buildSeasonToken();
    case "deck": return buildBridgeDeck();
    case "gateWall": return buildGateWall();
    case "dumpster": return buildDumpster();
    case "powerBox": return buildPowerBox();
    case "pole": return buildTrafficPole();
    case "bush": return buildBush();
    case "pillar": return buildPillar();
    default: return null;
  }
}

interface Parts {
  spin?: THREE.Object3D;
  float?: THREE.Object3D;
  glow?: THREE.Object3D;
  lamps: THREE.Object3D[];
  flames: THREE.Object3D[];
  underlight?: THREE.Object3D;
}
const partsCache = new WeakMap<THREE.Object3D, Parts>();

function parts(object: THREE.Object3D): Parts {
  let p = partsCache.get(object);
  if (!p) {
    const found: Parts = { lamps: [], flames: [] };
    object.traverse((o) => {
      if (o.name === "spin" && !found.spin) found.spin = o;
      else if (o.name === "float" && !found.float) found.float = o;
      else if (o.name === "glow" && !found.glow) found.glow = o;
      else if (/^lamp\d$/.test(o.name)) found.lamps.push(o);
      else if (o.name.startsWith("flame") && o.parent?.name !== "hull") found.flames.push(o);
      else if (o.name === "underlight" && !found.underlight) found.underlight = o;
    });
    partsCache.set(object, found);
    p = found;
  }
  return p;
}

/**
 * Per-frame animation for any prop built here (`t` = seconds, `phase` = a per-entity offset such as entity.id).
 * Coins spin (child "spin", same as the scene's old code), ETH coins wobble and pulse, pickups bob and turn (inner
 * "float"/"spin" — keep the group's own y fixed at yBase + 0.8 and don't rotate it), barrier and gate lamps blink,
 * ELON's light bar flashes (root.userData.fast = true while hunting), boards pulse their underglow. Others: no-op.
 */
export function animateProp(object: THREE.Object3D, t: number, phase = 0): void {
  const kind = object.userData.anim as string | undefined;
  if (!kind) return;
  const p = parts(object);
  switch (kind) {
    case "coin":
      if (p.spin) p.spin.rotation.y = t * 5.5 + phase * 0.7;
      break;
    case "ethCoin":
      if (p.spin) p.spin.rotation.y = Math.sin(t * 2 + phase) * 0.35;
      if (p.glow) p.glow.scale.setScalar(2.9 * (0.88 + 0.14 * Math.sin(t * 6 + phase)));
      break;
    case "pickup": {
      if (p.float) p.float.position.y = Math.sin(t * 3 + phase) * 0.15;
      if (p.spin) p.spin.rotation.y = object.userData.wobble ? Math.sin(t * 2.2 + phase) * 0.5 : t * 2 + phase;
      if (p.glow) p.glow.scale.setScalar(2.3 * (0.92 + 0.1 * Math.sin(t * 4 + phase)));
      p.flames.forEach((f, i) => { f.scale.set(1, 0.75 + 0.35 * Math.abs(Math.sin(t * 23 + i * 1.7)), 1); });
      break;
    }
    case "blink": {
      const on = Math.floor((t + phase * 0.13) * 2.5) % 2 === 0;
      p.lamps.forEach((l, i) => { l.visible = i % 2 === 0 ? on : !on; });
      break;
    }
    case "cop":
      animateGasCop(object, t, object.userData.fast === true);
      break;
    case "board":
      if (p.underlight) p.underlight.scale.setScalar(0.9 + 0.12 * Math.sin(t * 5 + phase));
      break;
  }
}
